import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert, Button, Card, Checkbox, Chip, Dialog, IconButton, PageHeader, Pill, Select, Skeleton, TextField, Textarea,
  scrollBehavior, useConfirm, useToast,
} from '../ui';
import { ArrowDownIcon, ArrowUpIcon, CameraIcon, ChevronDownIcon, EditIcon, LockIcon, PlusIcon, TrashIcon, WarningIcon } from '../ui/icons';
import api from '../config/api';
import {
  FIELD_TYPES, MEDIA_TYPES, IDENTIFIER_SCHEMES, MULTIPLE_ROLES, COMMON_UNITS, DEFAULT_CURRENCY, rolesFor, withTypeDefaults,
  fieldErrorMessage,
  canFilter, showsAsFilter, withFilter,
} from '../fieldTypes';
import { TagsInput } from './FieldInputs';
import NotFound from './NotFound';
import { VOCABULARIES, optionLabel, optionValue, presetInfo, uiLang, useKinds, useVocabulary } from '../kinds';
import { errorMessage } from '../errors';
import { useLeaveGuard } from '../shell/changes';
import { invalidateSavedViews } from '../shell/savedViews';
import { sectionCrumb } from '../settings/SectionLayout';
import { VERBS, personalOf } from './item/personal';

const EMPTY_FIELD = {
  name: '',
  type: 'text',
  required: false,
  description: '',
  options: [],
  discover_in_image: true,
  in_view: true,
};

const requiresOptions = (fieldType) => ['radio', 'select', 'checkbox'].includes(fieldType);
const fieldIncomplete = (field) =>
  (requiresOptions(field.type) && field.options.length === 0)
  || (field.type === 'measure' && (!field.units || field.units.length === 0));

/**
 * "Libellés d'affichage": the owner's own words for the codes of a model's
 * select or a vocabulary field (option_labels) — "SF" for Science-Fiction.
 * Stored values stay codes; only what is shown (and found) changes.
 */
function OptionLabelsEditor({ field, onChange }) {
  const { t, i18n } = useTranslation();
  const lang = uiLang(i18n);
  const vocabulary = useVocabulary(field.type === 'vocab' ? field.vocabulary : null);
  const entries = field.type === 'vocab'
    ? vocabulary.map(entry => ({ code: entry.code, label: entry.labels[lang] || entry.labels.en }))
    : (field.options || []).filter(option => option && typeof option === 'object')
      .map(option => ({ code: optionValue(option), label: optionLabel(option, lang) }));
  if (entries.length === 0) return null;
  const labels = field.option_labels || {};
  const set = (code, text) => {
    const next = { ...labels };
    if (text.trim() === '') delete next[code];
    else next[code] = text;
    onChange(Object.keys(next).length ? next : undefined);
  };
  return (
    <details className="option-labels" open={Object.keys(labels).length > 0}>
      <summary>{t('itemTypeForm.optionLabels.title')}<ChevronDownIcon className="option-labels__chevron" /></summary>
      <p className="ui-field__hint">{t('itemTypeForm.optionLabels.help')}</p>
      <div className="option-labels__grid">
        {entries.map(entry => (
          <TextField key={entry.code} label={entry.label} maxLength={100} value={labels[entry.code] || ''}
                     placeholder={entry.label} onChange={(e) => set(entry.code, e.target.value)} />
        ))}
      </div>
    </details>
  );
}

/** The form of one field: added to the list, or the one being edited put back. */
function FieldForm({
  field, setField, editing, editingName, takenRoles, savedType, onSubmit, onCancel, formRef, nameRef,
}) {
  const { t, i18n } = useTranslation();
  const lang = uiLang(i18n);
  const [option, setOption] = useState('');
  // A field of a kind's model: renamed, shown or hidden, reordered — nothing else (the server refuses).
  const locked = !!field.locked;
  // A saved field never changes to or from a photo field (the server refuses: its values are media ids).
  const typeAllowed = (type) => !savedType || type === savedType
    || (!MEDIA_TYPES.includes(savedType) && !MEDIA_TYPES.includes(type));
  // A type change fills the new type's options and drops a role it cannot play.
  const set = (name, value) => {
    const next = { ...field, [name]: value };
    setField(name === 'type' ? withTypeDefaults(next) : next);
  };
  const addOption = () => {
    if (!option.trim()) return;
    setField({ ...field, options: [...field.options, option.trim()] });
    setOption('');
  };
  const incomplete = fieldIncomplete(field);
  const submit = () => {
    if (field.name.trim() && !incomplete) onSubmit();
  };

  return (
    <Card as="section" padding="lg" ref={formRef} aria-labelledby="field-form-title"
          className={`type-editor__form${editing ? ' is-editing' : ''}`}
          // Enter in a text box adds (or updates) the field instead of saving the whole type.
          onKeyDown={(e) => {
            if (e.key !== 'Enter' || e.defaultPrevented || e.target.tagName !== 'INPUT' || e.target.type === 'checkbox') return;
            e.preventDefault();
            submit();
          }}>
      <h2 id="field-form-title" className="settings-card__title">
        {editing ? t('itemTypeForm.editFieldTitle', { name: editingName }) : t('itemTypeForm.newField')}
      </h2>
      <div className="dialog-form">
        <TextField ref={nameRef} label={t('itemTypeForm.fieldName')} name="name" value={field.name}
                   placeholder={t('itemTypeForm.fieldNamePlaceholder')} onChange={(e) => set('name', e.target.value)}
                   hint={field.key ? t('itemTypeForm.fieldKey', { key: field.key }) : undefined} />
        {locked && <p className="type-editor__note"><LockIcon size={16} /> {t('itemTypeForm.lockedField')}</p>}
        <div className="type-editor__pair">
          <Select label={t('itemTypeForm.fieldType')} value={field.type} disabled={locked}
                  hint={savedType && MEDIA_TYPES.includes(savedType) ? t('itemTypeForm.imageTypeLocked') : undefined}
                  onChange={(e) => set('type', e.target.value)}>
            {FIELD_TYPES.map(type => (
              <option key={type} value={type} disabled={!typeAllowed(type)}>{t(`itemTypeForm.typeOption.${type}`)}</option>
            ))}
          </Select>
          <Select label={t('itemTypeForm.role')} value={field.role || ''} disabled={locked} hint={t('itemTypeForm.roleHelp')}
                  onChange={(e) => set('role', e.target.value)}>
            <option value="">{t('itemTypeForm.noRole')}</option>
            {rolesFor(field.type).map(role => (
              <option key={role} value={role} disabled={takenRoles.includes(role)}>
                {t(`itemTypeForm.roleOption.${role}`)}{takenRoles.includes(role) ? ` (${t('itemTypeForm.roleTaken')})` : ''}
              </option>
            ))}
          </Select>
        </div>

        {field.type === 'rating' && (
          <Select label={t('itemTypeForm.ratingMax')} value={Number(field.max) === 5 ? 5 : 10} disabled={locked}
                  onChange={(e) => set('max', Number(e.target.value))} className="type-editor__short">
            <option value={5}>5</option>
            <option value={10}>10</option>
          </Select>
        )}
        {field.type === 'money' && (
          <TextField label={t('itemTypeForm.currency')} maxLength={3} value={field.currency || DEFAULT_CURRENCY} disabled={locked}
                     onChange={(e) => set('currency', e.target.value.toUpperCase())} className="type-editor__short type-editor__upper" />
        )}
        {field.type === 'measure' && (
          <div className="ui-field">
            <label className="ui-field__label" htmlFor="field-units">{t('itemTypeForm.units')}</label>
            {locked
              ? <p id="field-units">{(field.units || []).join(', ')}</p>
              : <TagsInput inputId="field-units" value={field.units || []} onChange={(units) => set('units', units)}
                           invalid={!(field.units || []).length} />}
            <p className="ui-field__hint">{t('itemTypeForm.unitsHelp', { examples: COMMON_UNITS.join(', ') })}</p>
          </div>
        )}
        {field.type === 'identifier' && (
          <Select label={t('itemTypeForm.scheme')} value={field.scheme || 'other'} disabled={locked}
                  onChange={(e) => set('scheme', e.target.value)}>
            {IDENTIFIER_SCHEMES.map(scheme => (
              <option key={scheme} value={scheme}>{t(`itemTypeForm.schemeOption.${scheme}`)}</option>
            ))}
          </Select>
        )}
        {field.type === 'vocab' && (
          <Select label={t('itemTypeForm.vocabulary')} value={field.vocabulary || VOCABULARIES[0]} disabled={locked}
                  onChange={(e) => setField({ ...field, vocabulary: e.target.value, option_labels: undefined })}>
            {VOCABULARIES.map(name => (
              <option key={name} value={name}>{t(`itemTypeForm.vocabularyOption.${name}`)}</option>
            ))}
          </Select>
        )}
        {(field.type === 'vocab' || (field.type === 'select' && (field.options || []).some(o => o && typeof o === 'object'))) && (
          <OptionLabelsEditor field={field} onChange={(labels) => set('option_labels', labels)} />
        )}

        <TextField label={t('itemTypeForm.fieldDescription')} name="description" value={field.description || ''}
                   placeholder={t('itemTypeForm.fieldDescriptionPlaceholder')} onChange={(e) => set('description', e.target.value)} />

        {/* Options of select, radio and checkbox fields */}
        {requiresOptions(field.type) && (
          <fieldset className="type-editor__options">
            <legend className="ui-field__label">{t('itemTypeForm.options')}</legend>
            {!locked && (
              <div className="type-editor__add-option">
                <TextField label={t('itemTypeForm.newOption')} hideLabel value={option} placeholder={t('itemTypeForm.optionPlaceholder')}
                           onChange={(e) => setOption(e.target.value)}
                           onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addOption(); } }} />
                <Button variant="secondary" icon={PlusIcon} onClick={addOption} disabled={!option.trim()}>{t('itemTypeForm.addOption')}</Button>
              </div>
            )}
            {field.options.length > 0 && (
              <ul className="tag-list type-editor__option-list">
                {field.options.map((value, index) => (
                  <li key={`${optionValue(value)}-${index}`}>
                    {locked
                      ? <Chip>{optionLabel(value, lang)}</Chip>
                      : <Chip onRemove={() => setField({ ...field, options: field.options.filter((_, i) => i !== index) })}
                              removeLabel={t('itemTypeForm.removeOption', { option: optionLabel(value, lang) })}>
                          {optionLabel(value, lang)}
                        </Chip>}
                  </li>
                ))}
              </ul>
            )}
            <p className="ui-field__hint">{field.type === 'checkbox' ? t('itemTypeForm.multipleChoice') : t('itemTypeForm.singleChoice')}</p>
          </fieldset>
        )}

        <div className="type-editor__checks">
          <Checkbox label={t('itemTypeForm.required')} checked={!!field.required} disabled={locked}
                    onChange={(e) => set('required', e.target.checked)} />
          {field.type !== 'image' && (  // photos are never read from an imported picture
            <Checkbox label={t('itemTypeForm.discoverInImage')} hint={t('itemTypeForm.discoverInImageHelp')}
                      checked={!!field.discover_in_image} onChange={(e) => set('discover_in_image', e.target.checked)} />
          )}
          <Checkbox label={t('itemTypeForm.showInView')} hint={t('itemTypeForm.showInViewHelp')}
                    checked={field.in_view !== false} onChange={(e) => set('in_view', e.target.checked)} />
          {canFilter(field) && (
            <Checkbox label={t('itemTypeForm.showAsFilter')} hint={t('itemTypeForm.showAsFilterHelp')}
                      checked={showsAsFilter(field)} onChange={(e) => setField(withFilter(field, e.target.checked))} />
          )}
          {field.role === 'year' && <p className="ui-field__hint">{t('itemTypeForm.showAsFilterYear')}</p>}
        </div>

        <div aria-live="polite">
          {requiresOptions(field.type) && field.options.length === 0 && <p className="ui-field__error">{t('itemTypeForm.needOption')}</p>}
          {field.type === 'measure' && (!field.units || field.units.length === 0) && <p className="ui-field__error">{t('itemTypeForm.needUnit')}</p>}
        </div>

        <div className="settings-actions">
          <Button variant={editing ? 'primary' : 'secondary'} icon={editing ? undefined : PlusIcon} onClick={submit}
                  disabled={!field.name.trim() || incomplete}>
            {editing ? t('itemTypeForm.updateField') : t('itemTypeForm.addField')}
          </Button>
          {editing && <Button variant="ghost" onClick={onCancel}>{t('itemTypeForm.cancelEdit')}</Button>}
        </div>
      </div>
    </Card>
  );
}

/** One field of the list: what it is, "show in list" / "used", and its buttons. */
function FieldRow({ field, index, count, editing, lang, onToggle, onMove, onEdit, onRemove }) {
  const { t } = useTranslation();
  const typeName = t(`fieldType.${field.type}`, { defaultValue: field.type });
  const summary = field.type === 'rating' ? t('itemTypeForm.ratingSummary', { max: Number(field.max) === 5 ? 5 : 10 })
    : field.type === 'money' ? t('itemTypeForm.currencySummary', { currency: field.currency || DEFAULT_CURRENCY })
      : field.type === 'measure' ? t('itemTypeForm.unitsSummary', { units: (field.units || []).join(', ') })
        : field.type === 'identifier' ? t(`itemTypeForm.schemeOption.${field.scheme || 'other'}`)
          : null;
  return (
    <li className={`field-row${field.unused ? ' is-unused' : ''}${editing ? ' is-editing' : ''}`}>
      <div className="field-row__main">
        <p className="field-row__name">
          {field.locked && <LockIcon size={16} title={t('itemTypeForm.lockedField')} className="field-row__lock" />}
          <strong>{field.name}</strong>
          {field.required && <span className="field-defs__required"><span aria-hidden="true">*</span><span className="visually-hidden">{t('itemTypeForm.requiredShort')}</span></span>}
          <span className="field-row__type">{typeName}</span>
          {field.role && <Pill tone="accent">{t(`itemTypeForm.roleOption.${field.role}`, { defaultValue: field.role })}</Pill>}
          {field.unused && <Pill>{t('itemTypeForm.unused.badge')}</Pill>}
          {field.discover_in_image && field.type !== 'image' && (
            <CameraIcon size={16} className="field-row__camera" title={t('itemTypeForm.discoverInImage')} />
          )}
        </p>
        {field.key && <p className="field-row__key">{t('itemTypeForm.fieldKey', { key: field.key })}</p>}
        {field.description && <p className="field-row__text">{field.description}</p>}
        {summary && <p className="field-row__text">{summary}</p>}
        {field.options && field.options.length > 0 && (
          <p className="field-row__text">
            {t('collection.options')} {field.options.slice(0, 3).map(option => optionLabel(option, lang)).join(', ')}
            {field.options.length > 3 && ` ${t('typeSelection.more', { count: field.options.length - 3 })}`}
          </p>
        )}
      </div>
      <div className="field-row__actions">
        <IconButton icon={ArrowUpIcon} variant="ghost" size="sm" disabled={index === 0}
                    label={t('itemTypeForm.moveUpNamed', { name: field.name })} onClick={() => onMove(index, -1)} />
        <IconButton icon={ArrowDownIcon} variant="ghost" size="sm" disabled={index === count - 1}
                    label={t('itemTypeForm.moveDownNamed', { name: field.name })} onClick={() => onMove(index, 1)} />
        <IconButton icon={EditIcon} variant="secondary" size="sm" className="field-row__edit"
                    label={t('itemTypeForm.editNamed', { name: field.name })} onClick={() => onEdit(index)} />
        <IconButton icon={TrashIcon} variant="ghost" size="sm" className="field-row__remove" disabled={!!field.locked}
                    title={field.locked ? t('itemTypeForm.lockedField') : undefined}
                    label={t('itemTypeForm.removeNamed', { name: field.name })} onClick={() => onRemove(index)} />
      </div>
      <div className="field-row__toggles">
        <Checkbox label={<>{t('itemTypeForm.showInView')}<span className="visually-hidden"> — {field.name}</span></>}
                  checked={field.in_view !== false} onChange={(e) => onToggle({ ...field, in_view: e.target.checked })} />
        {canFilter(field) && !field.unused && (
          <Checkbox label={<>{t('itemTypeForm.showAsFilter')}<span className="visually-hidden"> — {field.name}</span></>}
                    checked={showsAsFilter(field)} onChange={(e) => onToggle(withFilter(field, e.target.checked))} />
        )}
        {field.locked && (
          <Checkbox label={<>{t('itemTypeForm.unused.used')}<span className="visually-hidden"> — {field.name}</span></>}
                    hint={field.required ? t('itemTypeForm.unused.required') : undefined}
                    checked={!field.unused} disabled={!!field.required}
                    onChange={(e) => {
                      const next = { ...field };
                      if (e.target.checked) delete next.unused;
                      else next.unused = true;
                      onToggle(next);
                    }} />
        )}
      </div>
    </li>
  );
}

/**
 * /settings/item-types/new and /settings/item-types/:id — an item type's
 * name, description and fields, as a page (a type's fields are too many for
 * a dialog). The field form sits beside the list on a desktop and above it
 * elsewhere; editing a field brings the form into view and focuses its name.
 * Saving a type that collections use first shows what the change does to
 * their items, and offers to migrate them. Leaving with unsaved edits asks.
 */
function ItemTypeEditor({ itemTypes, loaded, onSaved }) {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const isNew = !id;
  const navigate = useNavigate();
  const toast = useToast();
  const confirm = useConfirm();
  const lang = uiLang(i18n);
  const kinds = useKinds();
  const itemType = isNew ? null : (itemTypes || []).find(type => String(type.id) === String(id));

  // A new type tracks nothing but may be rated (plain types, decision of 2026-10-13).
  const NEW_TYPE = { name: '', description: '', fields: [], personal: { done: null, rating: true } };
  const [formData, setFormData] = useState(isNew ? NEW_TYPE : null);
  const baseline = useRef(isNew ? JSON.stringify(NEW_TYPE) : null);
  const [currentField, setCurrentField] = useState(EMPTY_FIELD);
  const [editingFieldIndex, setEditingFieldIndex] = useState(null);
  // The field form sits above the list (beside it on a desktop): brought into view when a field is edited.
  const fieldFormRef = useRef(null);
  const fieldNameRef = useRef(null);
  const [migrationInfo, setMigrationInfo] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // The form starts from the type once it is known, and is not reset by a later refresh of the list.
  useEffect(() => {
    if (isNew || !itemType || formData) return;
    const start = {
      name: itemType.name || '',
      description: itemType.description || '',
      // Every field has in_view (true by default).
      fields: (itemType.fields || []).map(field => ({ ...field, in_view: field.in_view !== undefined ? field.in_view : true })),
      // What members keep for themselves: the type's, or its model's default.
      personal: personalOf(itemType),
    };
    baseline.current = JSON.stringify(start);
    setFormData(start);
  }, [isNew, itemType, formData]);

  const dirty = !!formData && JSON.stringify(formData) !== baseline.current;
  const askToLeave = useCallback(() => confirm({
    title: t('itemTypeForm.leave.title'),
    message: t('itemTypeForm.leave.message'),
    confirmLabel: t('itemTypeForm.leave.confirm'),
    cancelLabel: t('itemTypeForm.leave.stay'),
    danger: true,
  }), [confirm, t]);
  useLeaveGuard(dirty && !isSubmitting, askToLeave);

  if (!isNew && !itemType) {
    if (!loaded) return <Skeleton height={320} radius="var(--radius-lg)" />;
    return <NotFound />;
  }
  if (!isNew && !itemType.is_mine) return <NotFound />;
  // A conversion left unfinished: the type cannot change until it is finished, on its own page.
  if (!isNew && itemType.conversion_running) return <Navigate to={`/settings/item-types/${itemType.id}/convert`} replace />;
  if (!formData) return <Skeleton height={320} radius="var(--radius-lg)" />;

  const leave = () => navigate('/settings/item-types');
  const cancel = async () => {
    if (!dirty || await askToLeave()) leave();
  };
  const setFields = (fields) => setFormData({ ...formData, fields });

  // Roles already taken by another field (identifier may be shared).
  const takenRoles = formData.fields
    .filter((field, index) => index !== editingFieldIndex && field.role && !MULTIPLE_ROLES.includes(field.role))
    .map(field => field.role);
  const savedType = currentField.key
    ? ((itemType?.fields || []).find(field => field.key === currentField.key)?.type || null)
    : null;

  const resetField = () => {
    setEditingFieldIndex(null);
    setCurrentField(EMPTY_FIELD);
  };
  const submitField = () => {
    if (editingFieldIndex === null) {
      setFields([...formData.fields, withTypeDefaults(currentField)]);
    } else {
      const fields = [...formData.fields];
      fields[editingFieldIndex] = withTypeDefaults(currentField);
      setFields(fields);
    }
    resetField();
  };
  const editField = (index) => {
    setCurrentField({ options: [], ...formData.fields[index] });
    setEditingFieldIndex(index);
    requestAnimationFrame(() => {
      fieldFormRef.current?.scrollIntoView({ behavior: scrollBehavior(), block: 'start' });
      fieldNameRef.current?.focus({ preventScroll: true });
    });
  };
  const removeField = (index) => {
    setFields(formData.fields.filter((_, i) => i !== index));
    if (editingFieldIndex === index) resetField();
    else if (editingFieldIndex !== null && editingFieldIndex > index) setEditingFieldIndex(editingFieldIndex - 1);
  };
  const moveField = (index, delta) => {
    const target = index + delta;
    if (target < 0 || target >= formData.fields.length) return;
    const fields = [...formData.fields];
    [fields[index], fields[target]] = [fields[target], fields[index]];
    setFields(fields);
    if (editingFieldIndex === index) setEditingFieldIndex(target);
    else if (editingFieldIndex === target) setEditingFieldIndex(index);
  };
  const toggleField = (index, next) => {
    const fields = [...formData.fields];
    fields[index] = next;
    setFields(fields);
  };

  const payload = () => ({ ...formData, fields: formData.fields.map(withTypeDefaults) });
  const done = (saved, message) => {
    // Saved views keep only what the collections' fields still allow: read them again.
    invalidateSavedViews();
    baseline.current = JSON.stringify(formData);
    onSaved(saved);
    toast.success(message);
    leave();
  };

  const create = async () => {
    setIsSubmitting(true);
    try {
      const response = await api.post('/item-types', payload());
      done(response.data, t('itemTypeForm.created', { name: response.data.name }));
    } catch (error) {
      console.error('Error creating item type:', error);
      toast.error(t('errors.createItemType', { error: fieldErrorMessage(t, error, formData.fields) || errorMessage(t, error) }));
      setIsSubmitting(false);
    }
  };

  const update = async () => {
    setIsSubmitting(true);
    try {
      const response = await api.put(`/item-types/${itemType.id}`, payload());
      done(response.data, t('itemTypeForm.saved', { name: response.data.name }));
    } catch (error) {
      console.error('Error updating item type:', error);
      toast.error(t('errors.updateItemType', { error: fieldErrorMessage(t, error, formData.fields) || errorMessage(t, error) }));
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim() || isSubmitting) return;
    if (isNew) {
      create();
      return;
    }
    // A type collections use: first see what the change does to their items.
    setIsSubmitting(true);
    try {
      // The proposed fields: the answer also says what a migration would convert or drop.
      const response = await api.post(`/item-types/${itemType.id}/impact`, { fields: formData.fields });
      if (response.data.affected_inventories > 0) {
        setMigrationInfo(response.data);
        setIsSubmitting(false);
        return;
      }
    } catch (error) {
      const message = fieldErrorMessage(t, error, formData.fields);
      setIsSubmitting(false);
      if (message) {
        // The fields themselves are refused (role, type): the update would be too.
        toast.error(message);
        return;
      }
      // Without the preview the owner cannot see what would be converted or
      // dropped: stop and say why rather than updating blind.
      console.error('Error checking migration impact:', error);
      toast.error(t('itemTypeForm.migration.previewFailed', { error: errorMessage(t, error) }));
      return;
    }
    update();
  };

  const handleMigrationChoice = async (updateInventories) => {
    setMigrationInfo(null);
    if (!updateInventories) {
      update();
      return;
    }
    setIsSubmitting(true);
    try {
      const response = await api.put(`/item-types/${itemType.id}/migrate`, { ...payload(), update_inventories: true });
      const result = response.data.migrated_inventories;
      let message = t('itemTypeForm.saved', { name: formData.name });
      let warn = false;
      if (result) {
        const dropped = Object.values(result.conversions || {}).reduce((sum, c) => sum + (c.dropped || 0), 0);
        message = t('itemTypeForm.migrationDone', { migrated: result.migrated_count, count: result.total_items, errors: result.errors.length })
          + (dropped > 0 ? `\n${t('itemTypeForm.migration.droppedTotal', { count: dropped })}` : '');
        warn = dropped > 0 || result.errors.length > 0;
      }
      baseline.current = JSON.stringify(formData);
      // The migration's answer is a partial type: the list is fetched again.
      onSaved(response.data.item_type, { refetch: true });
      (warn ? toast.info : toast.success)(message);
      leave();
    } catch (error) {
      console.error('Error updating item type:', error);
      toast.error(t('errors.updateItemType', { error: fieldErrorMessage(t, error, formData.fields) || errorMessage(t, error) }));
      setIsSubmitting(false);
    }
  };

  // Per field: "Running time: text → measure — 812 values carried over, 3 dropped, 290 empty".
  const renderConversions = (all, title) => {
    // Fields left exactly as stored (same type and options) need no line.
    const conversions = Object.entries(all || {})
      .filter(([, c]) => c.from !== c.to || c.converted > 0 || c.dropped > 0);
    if (conversions.length === 0) return null;
    return (
      <div className="migration-block">
        <p className="dialog-preview__title">{title}</p>
        <ul>
          {conversions.map(([key, c]) => {
            const name = (formData.fields.find(f => f.key === key) || itemType.fields.find(f => f.key === key) || {}).name || key;
            return (
              <li key={key}>
                {c.to
                  ? t('itemTypeForm.migration.conversion', {
                    field: name,
                    from: t(`fieldType.${c.from}`, { defaultValue: c.from }),
                    to: t(`fieldType.${c.to}`, { defaultValue: c.to }),
                  })
                  : t('itemTypeForm.migration.removed', { field: name })}
                {' — '}
                {t('itemTypeForm.migration.converted', { count: c.converted })}
                {c.dropped > 0 && <>, <span className="migration-block__dropped">{t('itemTypeForm.migration.dropped', { count: c.dropped })}</span></>}
                {c.empty > 0 && <>, {t('itemTypeForm.migration.empty', { count: c.empty })}</>}
              </li>
            );
          })}
        </ul>
      </div>
    );
  };

  const preset = itemType?.preset && presetInfo(kinds, itemType.preset);
  const title = isNew ? t('itemTypes.create') : itemType.name;
  const crumbs = [sectionCrumb(t, 'settings'), { label: t('itemTypes.title'), to: '/settings/item-types' }, { label: title }];

  return (
    <form className="type-editor" onSubmit={handleSubmit}>
      <PageHeader breadcrumb={crumbs} title={title}>
        {(preset || !isNew) && (
          <p className="type-editor__subtitle">
            {preset && <Pill tone="accent" title={t('itemTypes.kindBadgeTitle')}>{preset.preset.labels[lang]}</Pill>}
            {!isNew && <span>{t('itemTypeForm.editTitle')}</span>}
          </p>
        )}
      </PageHeader>

      <div className="type-editor__layout">
        <Card as="section" padding="lg" aria-labelledby="type-general" className="type-editor__general">
          <h2 id="type-general" className="settings-card__title">{t('itemTypeForm.general')}</h2>
          <div className="dialog-form">
            <TextField label={t('itemTypeForm.name')} name="name" value={formData.name} required
                       placeholder={t('itemTypeForm.namePlaceholder')}
                       onChange={(e) => setFormData({ ...formData, name: e.target.value })} />
            <Textarea label={t('createCollection.description')} name="description" rows={3} value={formData.description}
                      placeholder={t('itemTypeForm.descriptionPlaceholder')}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })} />
          </div>
        </Card>

        <Card as="section" padding="lg" aria-labelledby="type-personal" className="type-editor__personal">
          <h2 id="type-personal" className="settings-card__title">{t('itemTypeForm.personal.title')}</h2>
          <p className="settings-muted">{t('itemTypeForm.personal.help')}</p>
          <div className="dialog-form">
            <Select label={t('itemTypeForm.personal.done')} value={formData.personal.done || ''}
                    onChange={(e) => setFormData({ ...formData, personal: { ...formData.personal, done: e.target.value || null } })}>
              <option value="">{t('itemTypeForm.personal.doneNone')}</option>
              {VERBS.map(verb => <option key={verb} value={verb}>{t(`itemTypeForm.personal.verbOption.${verb}`)}</option>)}
            </Select>
            <Checkbox label={t('itemTypeForm.personal.rating')} checked={formData.personal.rating}
                      onChange={(e) => setFormData({ ...formData, personal: { ...formData.personal, rating: e.target.checked } })} />
            <p className="ui-field__hint">{t('itemTypeForm.personal.kept')}</p>
          </div>
        </Card>

        <FieldForm
          field={currentField}
          setField={setCurrentField}
          editing={editingFieldIndex !== null}
          editingName={editingFieldIndex !== null ? formData.fields[editingFieldIndex]?.name || '' : ''}
          takenRoles={takenRoles}
          savedType={savedType}
          onSubmit={submitField}
          onCancel={resetField}
          formRef={fieldFormRef}
          nameRef={fieldNameRef}
        />

        <Card as="section" padding="lg" aria-labelledby="type-fields" className="type-editor__fields">
          <h2 id="type-fields" className="settings-card__title">{t('itemTypeForm.defined', { count: formData.fields.length })}</h2>
          {formData.fields.some(field => field.locked) && <p className="settings-muted">{t('itemTypeForm.unused.explain')}</p>}
          {formData.fields.length === 0 ? (
            <p className="settings-muted">{t('itemTypeForm.noFields')}</p>
          ) : (
            <ol className="field-list">
              {formData.fields.map((field, index) => (
                <FieldRow key={field.key || `new-${index}`} field={field} index={index} count={formData.fields.length}
                          editing={editingFieldIndex === index} lang={lang}
                          onToggle={(next) => toggleField(index, next)} onMove={moveField} onEdit={editField} onRemove={removeField} />
              ))}
            </ol>
          )}
        </Card>
      </div>

      <div className="item-form__bar type-editor__bar">
        <span className="item-form__status" aria-live="polite">{dirty ? t('itemTypeForm.unsaved') : ''}</span>
        <Button variant="secondary" onClick={cancel}>{t('common.cancel')}</Button>
        <Button type="submit" disabled={!formData.name.trim()} loading={isSubmitting}>
          {isNew ? t('itemTypeForm.create') : t('itemTypeForm.update')}
        </Button>
      </div>

      <Dialog
        open={!!migrationInfo}
        onClose={() => setMigrationInfo(null)}
        title={t('itemTypeForm.migration.title')}
        description={t('itemTypeForm.migration.intro')}
        size="lg"
        footer={(
          <>
            <Button variant="secondary" onClick={() => handleMigrationChoice(false)}>{t('itemTypeForm.migration.no')}</Button>
            <Button variant="danger" icon={WarningIcon} onClick={() => handleMigrationChoice(true)}>{t('itemTypeForm.migration.yes')}</Button>
          </>
        )}
      >
        {migrationInfo && (
          <div className="dialog-form">
            <div className="dialog-preview">
              <p className="dialog-preview__title">{t('itemTypeForm.migration.summary')}</p>
              <ul className="migration-block__list">
                <li>{t('itemTypeForm.migration.collections', { count: migrationInfo.affected_inventories })}</li>
                <li>{t('itemTypeForm.migration.items', { count: migrationInfo.affected_items })}</li>
              </ul>
              {renderConversions(migrationInfo.conversions, t('itemTypeForm.migration.conversions'))}
              {migrationInfo.stray_dropped > 0 && (
                <p className="migration-block__dropped">{t('itemTypeForm.migration.strays', { count: migrationInfo.stray_dropped })}</p>
              )}
              {renderConversions(migrationInfo.forced_conversions, t('itemTypeForm.migration.forced'))}
              {migrationInfo.sample_inventories.length > 0 && (
                <div className="migration-block">
                  <p className="dialog-preview__title">{t('itemTypeForm.migration.include')}</p>
                  <ul>
                    {migrationInfo.sample_inventories.map((name, index) => <li key={index}>{name}</li>)}
                    {migrationInfo.affected_inventories > migrationInfo.sample_inventories.length && (
                      <li><em>{t('locationTypes.more', { count: migrationInfo.affected_inventories - migrationInfo.sample_inventories.length })}</em></li>
                    )}
                  </ul>
                </div>
              )}
            </div>
            <Alert tone="warning" title={t('itemTypeForm.migration.notes')}>
              <ul className="migration-block__list">
                <li>{t('itemTypeForm.migration.note1')}</li>
                <li>{t('itemTypeForm.migration.note2')}</li>
                <li>{t('itemTypeForm.migration.note3')}</li>
                <li>{t('itemTypeForm.migration.note4')}</li>
              </ul>
            </Alert>
            <p><strong>{t('itemTypeForm.migration.question')}</strong></p>
          </div>
        )}
      </Dialog>
    </form>
  );
}

/** One editor per type: going from a type's page to another's starts afresh. */
function ItemTypeEditorRoute(props) {
  const { id } = useParams();
  return <ItemTypeEditor key={id || 'new'} {...props} />;
}

export default ItemTypeEditorRoute;
